import React from 'react';
import { TableRow, TableCell, Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { Visibility as ViewIcon, PersonAddAlt as AssignIcon, Description as QueryIcon } from '@mui/icons-material';
import { colors, fonts, spacing, tableStyles } from '../../constants';
import { getDisplayStatus, formatQueryType, getAssignorInfo } from '../../utils/assignedQueriesUtils';
import { formatDateTime } from '../../utils/dateUtils';

/**
 * AssignedQueryTableRow Component
 * Individual query row in table
 */
const AssignedQueryTableRow = ({
  query,
  onView,
  onAssign,
  getDisplayStatus,
  formatQueryType,
  getAssignorInfo,
  shouldShowAssignedByColumn,
  currentUser,
  currentUserRole,
  assigningId,
}) => {
  const currentUserId =
    currentUser?.id ||
    currentUser?._id ||
    currentUser?._Id ||
    null;
  const assigneeId =
    query.assignee?._id ||
    query.assignee?.id ||
    (typeof query.assignee === 'string' ? query.assignee : null);
  const isCurrentAssignee =
    currentUserId &&
    assigneeId &&
    assigneeId.toString().toLowerCase() === currentUserId.toString().toLowerCase();
  const normalizedStatus = (query.statusLabel || query.status || '').toString().trim().toLowerCase();
  const isSubmittedStatus = normalizedStatus === 'submitted';
  const isClosedStatus = query.isClosed === true || normalizedStatus === 'closed';
  const assignButtonDisabled =
    assigningId === query._id || !isCurrentAssignee || isSubmittedStatus || isClosedStatus;

  const statusInfo = getDisplayStatus(query);
  const assignorInfo = getAssignorInfo(query, currentUser);

  return (
    <TableRow
      key={query._id}
      hover
      sx={{
        ...tableStyles.row,
        backgroundColor: '#ffffff',
        transition: 'all 0.2s ease',
        borderLeft: `2px solid transparent`,
        borderTop: `1px solid ${colors.borderDefault}`,
        '& .MuiTableCell-root': {
          borderTop: `1px solid ${colors.borderDefault} !important`,
          borderBottom: `1px solid ${colors.borderDefault} !important`,
        },
        '&:hover': {
          backgroundColor: '#f8f9fa',
          borderLeft: `2px solid #0e172a`,
          borderTop: `1px solid ${colors.borderDefault}`,
          transform: 'translateX(2px)',
          '& .MuiTableCell-root': {
            borderTop: `1px solid ${colors.borderDefault} !important`,
            borderBottom: `1px solid ${colors.borderDefault} !important`,
          },
        },
      }}
    >
      <TableCell sx={{ ...tableStyles.cell, pl: spacing.lg, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
          <QueryIcon sx={{ fontSize: '14px', color: '#0e172a', opacity: 0.7 }} />
          <Typography sx={{ fontWeight: fonts.weightMedium, fontSize: '0.8125rem', color: '#1f2937' }}>
            {formatQueryType(query.queryType)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ ...tableStyles.cell, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography 
          sx={{ 
            fontWeight: fonts.weightMedium,
            color: '#1f2937',
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
          }}
        >
          {query.queryId || query._id}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableStyles.cell, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        {statusInfo ? (
          <Chip
            label={statusInfo.label}
            size="small"
            sx={{
              backgroundColor: `${statusInfo.color}1a`,
              color: statusInfo.color,
              fontWeight: fonts.weightSemiBold,
              fontSize: '0.7rem',
              alignSelf: 'flex-start',
            }}
          />
        ) : null}
      </TableCell>
      <TableCell sx={{ ...tableStyles.cell, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography sx={{ fontWeight: fonts.weightMedium, fontSize: '0.8125rem', color: '#1f2937' }}>
          {query.raisedBy?.full_name || query.raisedBy?.name || 'N/A'}
        </Typography>
      </TableCell>
      {shouldShowAssignedByColumn && (
        <TableCell sx={{ ...tableStyles.cell, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
          {(() => {
            if (!assignorInfo?.name) return 'N/A';

            return (
              <Tooltip
                title={
                  assignorInfo.designation
                    ? `${assignorInfo.name} (${assignorInfo.designation})`
                    : assignorInfo.name
                }
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#1f2937',
                    fontSize: fonts.fontSizeSm,
                    fontWeight: fonts.weightMedium,
                    cursor: assignorInfo.designation ? 'help' : 'default',
                  }}
                >
                  {assignorInfo.name}
                </Typography>
              </Tooltip>
            );
          })()}
        </TableCell>
      )}
      <TableCell sx={{ ...tableStyles.cell, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography 
          sx={{ 
            fontSize: '0.8125rem',
            color: '#6b7280',
          }}
        >
          {formatDateTime(query.raisedAt)}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableStyles.cell, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography 
          sx={{ 
            fontSize: '0.8125rem',
            color: '#6b7280',
          }}
        >
          {formatDateTime(query.slaDueDate)}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableStyles.cell, textAlign: 'right', width: '200px', minWidth: '200px', py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Box sx={{ display: 'flex', gap: spacing.xs, alignItems: 'center', justifyContent: 'flex-end' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onView(query);
              }}
              sx={{
                color: '#0e172a',
                '&:hover': { backgroundColor: colors.primaryTintHover },
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {(() => {
            const normalizedRole = (currentUserRole || '').toString().trim().toUpperCase();
            const isGM = normalizedRole === 'GM';
            if (isGM) return null;
            
            return (
              <Tooltip
                title={
                  assignButtonDisabled
                    ? isClosedStatus
                      ? 'Query is closed. Only reopening is allowed.'
                      : isSubmittedStatus
                      ? 'Status submitted. Assignments locked until Need Clarification.'
                      : 'Only the current assignee can assign'
                    : 'Delegate Query'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssign(query);
                    }}
                    disabled={assignButtonDisabled}
                    sx={{
                      color: colors.info,
                      '&:hover': {
                        backgroundColor: `${colors.info}10`,
                      },
                      '&.Mui-disabled': {
                        color: colors.textDisabled,
                      },
                    }}
                  >
                    <AssignIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            );
          })()}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default AssignedQueryTableRow;
